/**
 * Rows shared by the For You page and home: a windowed slider, the budget row
 * and the special rows (trending, new arrivals, recently viewed, ...). Moved
 * out of components/for-you-page.tsx so home ranks the same rows.
 */
import { useRef } from "react";
import type { ComponentProps } from "react";
import type { Swiper as SwiperType } from "swiper";
import ListingSlider from "./listing-slider";
import { useWindowedRow } from "../hooks/useWindowedRow";
import { useBudgetFindsListings } from "./useForYouApi";

// The For-You hooks return loosely-typed listing records; ListingSlider consumes
// the concrete ListingItem shape. Reference the slider's own prop type so this
// cast stays honest if that shape ever changes.
export type SliderList = ComponentProps<typeof ListingSlider>["list"];

/**
 * A For You row's slider, parked while it is far off screen.
 *
 * This is the one infinite feed with ads inside its rows (one every six cards),
 * and nothing on it used to unmount while the reader scrolled: every row kept
 * its cards, photos and ad frames for the life of the page view, and past the
 * page's ad budget new rows got no ads at all. Parked rows drop all three, so
 * the ad slots are destroyed (./ad-slot-reaper) and their budget goes to the
 * rows the reader is actually looking at.
 */
export const WindowedSlider = (props: ComponentProps<typeof ListingSlider>) => {
  const swiperRef = useRef<{ swiper: SwiperType } | null>(null);
  const { boxRef, parked, initialSlide } = useWindowedRow({
    enabled: true,
    hasCards: props.list.length > 0,
    swiperRef,
    keepKey: props.listName,
  });

  return (
    <div ref={boxRef} className="listing-slider-container" data-testid="foryou-row-slider">
      {parked ? null : <ListingSlider {...props} swiperRef={swiperRef} initialSlide={initialSlide} />}
    </div>
  );
};

// Budget Finds Showcase Component
export const BudgetFindsShowcase = () => {
  const { listings, isLoading } = useBudgetFindsListings();

  if (isLoading) {
    return (
      <div className="h-48 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (listings.length < 7) {
    return null; // Don't show if not enough listings
  }

  return (
    <ListingSlider
      list={listings as unknown as SliderList}
      listName="budget-finds"
      clickable={true}
      noMargin={true}
      adsPer={6}
      adsStart={6}
    />
  );
};

// Special Showcase Row - simple title + ListingSlider
//
// No "Not Interested" here on purpose. These rows are built from a price band or
// a recency window, not from a category or a tag, so there is no axis a negative
// could be recorded against -- the button would take a click and then have
// nowhere to put the answer.
export const SpecialShowcaseRow = ({ title, listings, listName }: {
  title: string;
  listings: Record<string, unknown>[];
  listName: string;
}) => {
  if (listings.length < 4) return null;

  return (
    <div className="mb-10">
      <div className="browse-heading-row">
        <h2 className="browse-heading">
          {title}
        </h2>
      </div>
      <WindowedSlider
          list={listings as unknown as SliderList}
          listName={listName}
          clickable={true}
          noMargin={true}
          adsPer={6}
          adsStart={6}
      />
    </div>
  );
};

